import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Lock, ChevronRight, Paperclip, Lightbulb, PenLine, ThumbsUp, ThumbsDown, Brain, Loader2, Info } from 'lucide-react';
import type { TimelineItem, AskItem, CaptureItem } from '../lib/types';
import { api, type Source } from '../lib/api';
import { Spinner, OwnershipBadge, Chip } from './ui';
import { useFlyTranslate, TranslateButton } from './Translate';

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-accent px-4 py-2 text-accent-fg">
        {text}
      </div>
    </div>
  );
}

/** Pastille « capturé par [Nom] » (CHAT-06) : met en valeur la capture humaine.
 *  Cliquable → page de l'auteur (tout ce qu'il a capturé). */
function CapturedByBadge({ name, id }: { name: string; id?: string }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (id) location.hash = `#/author/${encodeURIComponent(id)}`;
      }}
      title={t('source.capturedByHint', { name })}
      className="flex shrink-0 items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent ring-1 ring-accent/20 transition hover:bg-accent/20"
    >
      <PenLine className="h-3 w-3" /> <span className="max-w-[8rem] truncate">{name}</span>
    </button>
  );
}

function SourceCard({ s }: { s: Source }) {
  // Traduction à la volée du contenu si le grain est dans une autre langue (TRAD).
  const tr = useFlyTranslate(s.excerpt, s.lang);
  return (
    <details className="group rounded-lg border border-border bg-surface-2 px-3 py-2">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
        <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-accent/15 text-xs text-accent">
          {s.n}
        </span>
        <span className="min-w-0 flex-1 truncate">{s.title}</span>
        {s.capturedBy && <CapturedByBadge name={s.capturedBy} id={s.capturedById} />}
      </summary>
      <p className="mt-2 text-sm text-muted">{tr.display}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {s.entities.map((e) => (
          <OwnershipBadge key={e.name} ownership={e.ownership} label={`${e.name}`} />
        ))}
        {s.themes.map((th) => (
          <Chip key={th}># {th}</Chip>
        ))}
        {tr.available && (
          <span className="ml-auto">
            <TranslateButton busy={tr.busy} on={tr.on} onClick={tr.toggle} />
          </span>
        )}
      </div>
    </details>
  );
}

const SOURCES_OPEN_KEY = 'gnosis.sourcesOpen';

/**
 * Les sources sont repliées par défaut (gagne de la place) ; le choix
 * ouvert/fermé de l'utilisateur est mémorisé (localStorage) et s'applique
 * aux réponses suivantes.
 */
function SourcesSection({ sources }: { sources: Source[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SOURCES_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  return (
    <details
      open={open}
      onToggle={(e) => {
        const o = e.currentTarget.open;
        setOpen(o);
        try {
          localStorage.setItem(SOURCES_OPEN_KEY, o ? '1' : '0');
        } catch {
          /* stockage indisponible : on garde le choix en mémoire de session */
        }
      }}
      className="group space-y-1.5"
    >
      <summary className="flex cursor-pointer list-none items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted hover:text-fg">
        <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
        {t('answer.sources')} · {sources.length}
      </summary>
      <div className="space-y-1.5 pt-1.5">
        {sources.map((s) => (
          <SourceCard key={s.id} s={s} />
        ))}
      </div>
    </details>
  );
}

/** Invite à compléter la base (CHAT-03) : prominente sur un « trou », discrète sinon. */
function CompleteCta({ gap, question, onComplete }: { gap: boolean; question: string; onComplete: (q: string) => void }) {
  const { t } = useTranslation();
  if (gap) {
    return (
      <button
        onClick={() => onComplete(question)}
        className="flex w-full items-center gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-left text-sm text-amber-700 ring-1 ring-amber-500/30 transition hover:bg-amber-500/20 dark:text-amber-300"
      >
        <Lightbulb className="h-4 w-4 shrink-0" />
        <span className="flex-1">
          <span className="font-medium">{t('answer.gapTitle')}</span> — {t('answer.gapCta')}
        </span>
        <PenLine className="h-4 w-4 shrink-0" />
      </button>
    );
  }
  return (
    <button onClick={() => onComplete(question)} className="flex items-center gap-1.5 text-xs text-muted hover:text-fg">
      <PenLine className="h-3.5 w-3.5" /> {t('answer.complete')}
    </button>
  );
}

/** Barre d'actions sous une réponse : retour 👍/👎 (D) + réponse approfondie (CHAT-04). */
function Actions({
  item,
  isAdmin,
  onDeepen,
  onFeedback,
}: {
  item: AskItem;
  isAdmin: boolean;
  onDeepen: (item: AskItem) => void;
  onFeedback: (item: AskItem, value: 1 | -1) => void;
}) {
  const { t } = useTranslation();
  const r = item.response;
  if (!r) return null;
  // Auteurs uniques cités dans cette réponse (capture humaine) → pastilles en bas (CHAT-06).
  const authors = Array.from(
    new Map(
      r.sources.filter((s) => s.capturedBy).map((s) => [s.capturedById ?? s.capturedBy, { name: s.capturedBy!, id: s.capturedById }]),
    ).values(),
  );
  const fbBtn = (v: 1 | -1, Icon: typeof ThumbsUp, label: string) => (
    <button
      onClick={() => onFeedback(item, v)}
      aria-pressed={item.feedback === v}
      title={label}
      aria-label={label}
      className={`grid h-7 w-7 place-items-center rounded-lg transition ${
        item.feedback === v ? 'bg-accent/15 text-accent' : 'text-muted hover:bg-surface-2 hover:text-fg'
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {r.logId && (
        <div className="flex items-center gap-0.5">
          {fbBtn(1, ThumbsUp, t('answer.helpful'))}
          {fbBtn(-1, ThumbsDown, t('answer.notHelpful'))}
        </div>
      )}
      {/* Attribution en bas à gauche : qui a capturé les sources humaines de cette réponse. */}
      {authors.map((a) => (
        <CapturedByBadge key={a.id ?? a.name} name={a.name} id={a.id} />
      ))}
      {/* Réponse approfondie : réservée aux admins (coûte plus de crédits). */}
      {isAdmin &&
        (item.deepened ? (
          <span className="flex items-center gap-1 text-muted">
            <Brain className="h-3.5 w-3.5" /> {t('answer.deepened')}
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <button
              onClick={() => onDeepen(item)}
              disabled={item.deepening}
              title={t('answer.deepenHint')}
              className="flex items-center gap-1.5 rounded-lg bg-surface-2 px-2.5 py-1 text-muted transition hover:text-fg disabled:opacity-50"
            >
              {item.deepening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
              {item.deepening ? t('answer.deepening') : t('answer.deepen')}
            </button>
            {/* Honnêteté de coût : on prévient que l'approfondissement consomme plus de crédits (sans afficher de prix). */}
            <Info className="h-3.5 w-3.5 cursor-help text-muted/70" aria-label={t('answer.deepenHint')}>
              <title>{t('answer.deepenHint')}</title>
            </Info>
          </span>
        ))}
    </div>
  );
}

function AskCard({
  item,
  isAdmin,
  onComplete,
  onDeepen,
  onFeedback,
}: {
  item: AskItem;
  isAdmin: boolean;
  onComplete: (q: string) => void;
  onDeepen: (item: AskItem) => void;
  onFeedback: (item: AskItem, value: 1 | -1) => void;
}) {
  const { t } = useTranslation();
  // La réponse est synthétisée en français (SYSTEM_ANSWER) ; le bouton « Traduire »
  // n'apparaît donc que si l'interface n'est pas en français (TRAD-01).
  const tr = useFlyTranslate(item.response?.answer ?? '', 'fr');
  return (
    <div className="space-y-3">
      <UserBubble text={item.question} />
      <div className="max-w-[90%] space-y-3">
        {item.status === 'loading' && <Spinner label={t('answer.thinking')} />}
        {item.status === 'error' && <ErrorLine code={item.errorCode} />}
        {item.status === 'done' && item.response && (
          <>
            <div className="md rounded-2xl rounded-bl-sm bg-surface px-4 py-3 leading-relaxed shadow-sm ring-1 ring-border">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{tr.display}</ReactMarkdown>
              {tr.available && (
                <div className="mt-2 flex border-t border-border pt-2">
                  <TranslateButton busy={tr.busy} on={tr.on} onClick={tr.toggle} />
                </div>
              )}
            </div>
            <CompleteCta gap={!!item.response.gap} question={item.question} onComplete={onComplete} />
            <Actions item={item} isAdmin={isAdmin} onDeepen={onDeepen} onFeedback={onFeedback} />
            {item.response.sources.length > 0 && <SourcesSection sources={item.response.sources} />}
            {item.response.model && (
              <details className="group ml-auto w-fit">
                <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-muted hover:text-fg">
                  <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                  {t('answer.model')}
                </summary>
                <p className="pt-1 text-right text-xs text-muted">{item.response.model}</p>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CaptureCard({ item }: { item: CaptureItem }) {
  const { t } = useTranslation();
  const r = item.response;
  const redCount = r?.redactions.reduce((a, x) => a + x.count, 0) ?? 0;
  return (
    <div className="space-y-3">
      <UserBubble text={item.text} />
      <div className="max-w-[90%]">
        {item.status === 'loading' && <Spinner label={t('answer.thinking')} />}
        {item.status === 'error' && <ErrorLine code={item.errorCode} />}
        {item.status === 'done' && r && (
          <div className="space-y-2 rounded-2xl rounded-bl-sm bg-surface px-4 py-3 shadow-sm ring-1 ring-border">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="h-4 w-4" />
              {r.status === 'PENDING_ENRICH' ? t('capture.pending') : t('capture.saved')}
            </div>
            <p className="font-medium">{r.title}</p>
            <p className="text-sm text-muted">{r.bodyClean}</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {r.entities.map((e) => (
                <OwnershipBadge key={e.entity.name} ownership={e.entity.ownership} label={e.entity.name} />
              ))}
              {r.themes.map((th) => (
                <Chip key={th.theme.label}># {th.theme.label}</Chip>
              ))}
            </div>
            {r.attachments && r.attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {r.attachments.map((a) => (
                  <a
                    key={a.id}
                    href={api.attachmentUrl(r.id, a.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-xs text-muted hover:text-fg"
                  >
                    <Paperclip className="h-3 w-3" /> <span className="max-w-[12rem] truncate">{a.filename}</span>
                  </a>
                ))}
              </div>
            )}
            {redCount > 0 && (
              <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <Lock className="h-3.5 w-3.5" /> {t('capture.redacted', { count: redCount })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorLine({ code }: { code?: string }) {
  const { t } = useTranslation();
  const msg = code === 'network' ? t('error.network') : t('error.generic');
  return (
    <p className="rounded-lg bg-surface px-4 py-2 text-sm text-red-600 ring-1 ring-border dark:text-red-400" role="alert">
      {msg}
    </p>
  );
}

export function Thread({
  items,
  isAdmin,
  onComplete,
  onDeepen,
  onFeedback,
}: {
  items: TimelineItem[];
  isAdmin: boolean;
  onComplete: (q: string) => void;
  onDeepen: (item: AskItem) => void;
  onFeedback: (item: AskItem, value: 1 | -1) => void;
}) {
  return (
    <div className="space-y-6">
      {items.map((it) =>
        it.kind === 'ask' ? (
          <AskCard key={it.id} item={it} isAdmin={isAdmin} onComplete={onComplete} onDeepen={onDeepen} onFeedback={onFeedback} />
        ) : (
          <CaptureCard key={it.id} item={it} />
        ),
      )}
    </div>
  );
}
